import { z } from 'zod';

import { Email, I18nText, IsoDate, Uid } from '../primitives';

/**
 * Platform settings (docs/06 §2.14, Figma CMS-16). One D1 `settings` row per key, value
 * JSON-encoded. Keys are stable strings — the CMS edits them in one screen and the API writes
 * them in one audited `PUT`, so the contract is a closed object rather than an open bag.
 */
export const SETTING_KEYS = [
  'support_email',
  'controls_timeout_ms',
  'session_hours',
  'billing.checkout_enabled',
  'entitlements.enforce',
  'drm.enforce',
  'maintenance_banner',
  'auth.social_enabled',
] as const;
export type SettingKey = (typeof SETTING_KEYS)[number];

/** Maintenance banner shown over Home on TV and mobile. */
export const MaintenanceBanner = z.object({ enabled: z.boolean(), text: I18nText });
export type MaintenanceBanner = z.infer<typeof MaintenanceBanner>;

/**
 * Every setting with its validation rule. `controls_timeout_ms` hides the player HUD
 * (docs/04 §7.5), `session_hours` is `sessions.expires_at - started_at` (docs/02 §1) and the
 * flags gate payments (docs/07 §2), entitlements (docs/07 §1), DRM (docs/07 §4) and the social
 * login providers (docs/05 §1; phase 1 keeps them off).
 */
export const CmsSettings = z.object({
  support_email: Email,
  controls_timeout_ms: z.number().int().min(1000).max(600_000),
  session_hours: z.number().int().min(1).max(72),
  'billing.checkout_enabled': z.boolean(),
  'entitlements.enforce': z.boolean(),
  'drm.enforce': z.boolean(),
  maintenance_banner: MaintenanceBanner,
  'auth.social_enabled': z.boolean(),
});
export type CmsSettings = z.infer<typeof CmsSettings>;

/**
 * Values used when the `settings` table has no row for a key (fresh environment). Phase 1
 * (docs/07 §1–§3): no checkout, no social login, nothing enforced.
 */
export const CMS_SETTINGS_DEFAULTS: CmsSettings = {
  support_email: 'support@acapella.lol',
  controls_timeout_ms: 10_000,
  session_hours: 6,
  'billing.checkout_enabled': false,
  'entitlements.enforce': false,
  'drm.enforce': false,
  maintenance_banner: { enabled: false, text: { en: '', es: '' } },
  'auth.social_enabled': false,
};

/** `GET /cms/settings` */
export const CmsSettingsResponse = z.object({
  settings: CmsSettings,
  /** Most recent `settings.updated_at` across the stored keys; null when nothing is stored. */
  updatedAt: IsoDate.nullable(),
  updatedBy: Uid.nullable(),
});
export type CmsSettingsResponse = z.infer<typeof CmsSettingsResponse>;

/** `PUT /cms/settings` — partial, at least one key; unknown keys are rejected. */
export const CmsSettingsPutBody = CmsSettings.partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one setting is required' });
export type CmsSettingsPutBody = z.infer<typeof CmsSettingsPutBody>;
export type CmsSettingsPutBodyInput = z.input<typeof CmsSettingsPutBody>;
